import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarGaugeProps {
  /** Rating value (e.g. 3.9). Clamped to [0, max]. */
  value: number;
  max?: number;
  /** Star edge length in px. */
  size?: number;
  className?: string;
}

/**
 * 5-star rating gauge with a partially-filled final star. A filled row is
 * overlaid on an empty row and clipped to `value/max` width, so the boundary
 * star fills proportionally to the decimal (e.g. 3.9 → ~90% of the 4th star).
 * Token-driven (warning scale for fill) — no bespoke colours.
 */
export function StarGauge({ value, max = 5, size = 18, className }: StarGaugeProps) {
  const pct = Math.max(0, Math.min(1, max > 0 ? value / max : 0)) * 100;

  const row = (filled: boolean) => (
    <div className="flex gap-[3px]">
      {Array.from({ length: max }).map((_, i) => (
        <Star
          key={i}
          width={size}
          height={size}
          style={
            filled
              ? { fill: 'var(--warning-500)', color: 'var(--warning-500)' }
              : { fill: 'none', color: 'var(--muted-foreground)', opacity: 0.35 }
          }
        />
      ))}
    </div>
  );

  return (
    <div
      className={cn('relative inline-flex w-fit', className)}
      role="img"
      aria-label={`${value.toFixed(1)} out of ${max}`}
    >
      {row(false)}
      <div className="absolute left-0 top-0 overflow-hidden" style={{ width: `${pct}%` }}>
        {row(true)}
      </div>
    </div>
  );
}
